// CSV imports for the knowledge base: question/answer pairs and product
// catalogues. A small RFC 4180 parser (quoted fields, doubled quotes, CR/LF,
// a BOM) and the mapping from columns to entries. Pure; shared by the server
// (which imports) and the admin UI (which previews). See csv.test.ts.

import type { FaqPair } from './faq';
import type { Product } from './product';

/** Rows of fields. Blank lines are dropped; ragged rows are kept as they are. */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^\uFEFF/, '');
  // Excel in many locales writes ';'-separated "CSV": pick whichever of , ; tab
  // appears most on the first line.
  const firstLine = src.slice(0, src.search(/\r?\n/) === -1 ? src.length : src.search(/\r?\n/));
  const counts = [',', ';', '\t'].map((d) => [d, firstLine.split(d).length - 1] as const);
  const delim = counts.reduce((best, c) => (c[1] > best[1] ? c : best))[0];

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"' && field === '') quoted = true;
    else if (ch === delim) {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      if (row.some((f) => f.trim())) rows.push(row);
      row = [];
      field = '';
    } else field += ch;
  }
  row.push(field);
  if (row.some((f) => f.trim())) rows.push(row);
  return rows;
}

const norm = (h: string) => h.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Index of the first header cell matching one of `names` (lowercase words,
 *  punctuation ignored), or -1. */
export function columnIndex(header: string[], names: readonly string[]): number {
  const h = header.map(norm);
  for (const n of names) {
    const i = h.indexOf(n);
    if (i !== -1) return i;
  }
  return -1;
}

export interface CsvImport<T> {
  items: T[];
  /** Data rows that could not be used, 1-based as a spreadsheet numbers them. */
  skipped: Array<{ row: number; reason: string }>;
}

const QUESTION = ['question', 'questions', 'q', 'query', 'title'];
const ANSWER = ['answer', 'answers', 'a', 'response', 'reply'];

/**
 * Question/answer pairs. Uses the "question"/"answer" header when there is one;
 * without a header, the first two columns.
 */
export function csvToFaq(rows: string[][]): CsvImport<FaqPair> {
  if (rows.length === 0) return { items: [], skipped: [] };
  let q = columnIndex(rows[0]!, QUESTION);
  let a = columnIndex(rows[0]!, ANSWER);
  const hasHeader = q !== -1 && a !== -1;
  if (!hasHeader) {
    q = 0;
    a = 1;
  }
  const items: FaqPair[] = [];
  const skipped: CsvImport<FaqPair>['skipped'] = [];
  rows.slice(hasHeader ? 1 : 0).forEach((r, i) => {
    const rowNo = i + (hasHeader ? 2 : 1);
    const question = (r[q] ?? '').replace(/\s+/g, ' ').trim();
    const answer = (r[a] ?? '').trim();
    if (!question || !answer) skipped.push({ row: rowNo, reason: 'needs both a question and an answer' });
    else items.push({ question, answer });
  });
  return { items, skipped };
}

const PRODUCT_COLUMNS = {
  name: ['name', 'product', 'product name', 'title'],
  description: ['description', 'details', 'body', 'body html', 'summary'],
  price: ['price', 'cost', 'mrp', 'variant price'],
  url: ['url', 'link', 'product url', 'page', 'permalink'],
  sku: ['sku', 'id', 'product id', 'variant sku', 'code'],
  category: ['category', 'type', 'product type', 'collection'],
  availability: ['availability', 'stock', 'in stock', 'inventory'],
} as const;

/** A product catalogue with a header row; needs at least a name column. */
export function csvToProducts(rows: string[][]): CsvImport<Product> & { missingHeader?: true } {
  if (rows.length === 0) return { items: [], skipped: [] };
  const header = rows[0]!;
  const idx = Object.fromEntries(
    Object.entries(PRODUCT_COLUMNS).map(([k, names]) => [k, columnIndex(header, [...names])]),
  ) as Record<keyof typeof PRODUCT_COLUMNS, number>;
  if (idx.name === -1) return { items: [], skipped: [], missingHeader: true };
  const known = new Set(Object.values(idx).filter((i) => i !== -1));
  const items: Product[] = [];
  const skipped: CsvImport<Product>['skipped'] = [];
  rows.slice(1).forEach((r, i) => {
    const cell = (k: keyof typeof PRODUCT_COLUMNS) => (idx[k] === -1 ? '' : (r[idx[k]] ?? '').trim());
    const name = cell('name').replace(/\s+/g, ' ');
    if (!name) {
      skipped.push({ row: i + 2, reason: 'no product name' });
      return;
    }
    const url = cell('url');
    // Any other column is kept as a labelled detail (colour, size, brand…).
    const extra = header
      .map((h, c) => [h.trim(), (r[c] ?? '').trim()] as const)
      .filter(([h, v], c) => !known.has(c) && h && v);
    items.push({
      name,
      description: stripTags(cell('description')),
      price: cell('price'),
      url: /^https?:\/\//i.test(url) ? url : '',
      sku: cell('sku'),
      category: cell('category'),
      availability: cell('availability'),
      details: extra.map(([label, value]) => ({ label, value })),
    });
  });
  return { items, skipped };
}

/** Shop exports often put HTML in descriptions. */
function stripTags(html: string): string {
  return html
    .replace(/<(br|\/p|\/li|\/div)\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
